import { useCallback, useEffect, useRef, useState } from 'react';

const usePaginatedConversations = ({ chatService, isVisible, limit = 10 }) => {
  const [conversations, setConversations] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'loadingMore' | 'error'
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  // Track if component is mounted
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  /**
   * Parse API response to extract conversations and pagination info
   */
  const parseResponse = useCallback(
    (response) => {
      // Handle array response
      if (Array.isArray(response)) {
        return {
          items: response,
          hasMoreData: response.length === limit,
        };
      }

      // Handle object response with conversations array
      if (response && typeof response === 'object' && response.conversations) {
        return {
          items: response.conversations,
          hasMoreData: response.hasMore !== undefined ? response.hasMore : response.conversations.length === limit,
        };
      }

      // Default empty response
      return { items: [], hasMoreData: false };
    },
    [limit]
  );

  /**
   * Load initial conversations
   */
  const loadInitialConversations = useCallback(async () => {
    if (!chatService || status === 'loading' || status === 'loadingMore') return;

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setStatus('loading');
    setError(null);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const response = await chatService.getAllConversations({
        limit,
        offset: 0,
      });

      if (!isMountedRef.current) return;

      const { items, hasMoreData } = parseResponse(response);

      setConversations(items);
      setOffset(limit);
      setHasMore(hasMoreData);
      setStatus('idle');
    } catch (err) {
      if (!isMountedRef.current || err.name === 'AbortError') return;

      setError('Failed to load conversations');
      setConversations([]);
      setHasMore(false);
      setStatus('error');
    } finally {
      if (isMountedRef.current) {
        abortControllerRef.current = null;
      }
    }
  }, [chatService, status, limit, parseResponse]);

  /**
   * Load more conversations for pagination
   */
  const loadMoreConversations = useCallback(async () => {
    // Don't load more if already loading, no more data, or no conversations yet
    if (!chatService || !hasMore || status !== 'idle' || conversations.length === 0) return;

    setStatus('loadingMore');

    try {
      const response = await chatService.getAllConversations({
        limit,
        offset,
      });

      if (!isMountedRef.current) return;

      const { items, hasMoreData } = parseResponse(response);

      setConversations((prev) => [...prev, ...items]);
      setOffset((prev) => prev + limit);
      setHasMore(hasMoreData);
      setStatus('idle');
    } catch (err) {
      if (!isMountedRef.current || err.name === 'AbortError') return;

      setError('Failed to load more conversations');
      setStatus('error');
    }
  }, [chatService, hasMore, status, conversations.length, limit, offset, parseResponse]);

  /**
   * Add a new conversation to the list
   */
  const addConversation = useCallback((conversation) => {
    setConversations((prev) => [conversation, ...prev]);
  }, []);

  /**
   * Update an existing conversation
   */
  const updateConversation = useCallback((conversationId, updates) => {
    setConversations((prev) => prev.map((conv) => (conv.id === conversationId ? { ...conv, ...updates } : conv)));
  }, []);

  /**
   * Load initial conversations when component becomes visible
   */
  useEffect(() => {
    if (isVisible && conversations.length === 0 && status === 'idle' && hasMore) {
      loadInitialConversations();
    }
  }, [isVisible, conversations.length, status, hasMore, loadInitialConversations]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Abort any active requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    // State
    conversations,
    isLoading: status === 'loading',
    isLoadingMore: status === 'loadingMore',
    error,
    hasMore,

    // Methods
    loadMore: loadMoreConversations,
    refresh: loadInitialConversations,
    addConversation,
    updateConversation,
  };
};

export default usePaginatedConversations;
